import { ModalContext } from 'app/app';
import axios from 'axios';
import { IconedButton } from 'components/IconedButton';
import { Pagination } from 'components/Pagination';
import {
  Animate,
  Badge,
  Grid,
  Loader,
  Search,
  Table,
  ViewContext,
} from 'components/lib';
import DateUtils from 'helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';

export function AgencyOverview(props) {
  const { id } = useParams();
  const [payload, setPayload] = useState();
  const [agencyData, setAgencyData] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);

  const format = new DateUtils();
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  const { setModal } = useContext(ModalContext);
  const context = useContext(ViewContext);

  async function runAPI(page, search) {
    setLoading(true);

    try {
      const [accountRes, usersRes] = await Promise.all([
        axios.get(`/api/account/${id}`),
        axios.get(`/api/user/search?page=${page}&search=${search}&agency_id=${id}`)
      ]);

      const accountData = accountRes.data.data;
      if (accountData.status === 'total_disabled') {
        setIsDisabled(true);
      }
      setAgencyData(accountData);

      const payload = usersRes.data.data;
      setPayload(payload);

      const timezone = await format.getTimezone();

      const invitedUsers = await axios.get(`/api/invite/account/${id}`);

      const tempData = [];

      if (invitedUsers.data.data) {
        invitedUsers.data.data.forEach((value) => {
          tempData.push({
            id: value.id,
            name:
              value.type === 'user'
                ? value.first_name + ' ' + value.last_name
                : value.owner_name,
            email: value.email,
            createdAt: 'N/A',
            status: 'Invited',
            permission: value.permission ? (
              <Badge
                text={value.permission}
                color={
                  value.permission === 'owner' ? 'orange' : 'blue'
                }
              />
            ) : (
              'N/A'
            ),
          });
        });
      }

      payload.data.forEach((value) => {
        tempData.push({
          id: value.id,
          name: value.name,
          email: value.email,
          createdAt: format.timestamp(value.date_created, timezone),
          status: value.disabled ? 'Inactive' : 'Active',
          permission: value.account.find((acc) => acc.id === id) ? (
            <Badge
              text={
                value.account.find((acc) => acc.id === id)
                  .permission
              }
              color={
                value.account.find((acc) => acc.id === id)
                  .permission === 'owner'
                  ? 'orange'
                  : 'blue'
              }
            />
          ) : (
            'N/A'
          ),
        });
      });

      setUsers(tempData);

    } catch (err) {
      console.error("Error loading agency data:", err);

      context.notification.show(
        'Failed to load agency data. Please try again.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    runAPI(page, '');
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Animate type="pop">
            <div
              className={styles.agencyOverview}
              style={{ marginBottom: 20 }}
            >
              <div className={styles.left}>
                <div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Company name:</span>
                      <span>
                        {agencyData.name ? agencyData.name : ''}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>DBA Name:</span>
                      <span>
                        {agencyData.dba_name
                          ? agencyData.dba_name
                          : 'Not found'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Owner name:</span>
                      <span>
                        {agencyData.owner_name
                          ? agencyData.owner_name
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Email:</span>
                      <span>
                        {agencyData.owner_email
                          ? agencyData.owner_email
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Address line 1:</span>
                      <span>
                        {agencyData.address1
                          ? agencyData.address1
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Address line 2:</span>
                      <span>
                        {agencyData.address2
                          ? agencyData.address2
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Phone:</span>
                      <span>
                        {agencyData.certificate_contacts &&
                          agencyData.certificate_contacts[0] &&
                          agencyData.certificate_contacts[0].phone
                          ? agencyData.certificate_contacts[0].phone
                          : 'Not found'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>City:</span>
                      <span>
                        {agencyData.city ? agencyData.city : ''}
                      </span>
                    </div>
                    <div className={styles.info}>
                      <span>State:</span>
                      <span>
                        {agencyData.state ? agencyData.state : ''}
                      </span>
                    </div>
                    <div className={styles.info}>
                      <span>ZIP:</span>
                      <span>
                        {agencyData.zip ? agencyData.zip : ''}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.stats}>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Active Policies:</span>
                      <span>
                        {agencyData.active_policies
                          ? agencyData.active_policies
                          : 0}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Active Premium:</span>
                      <span>
                        {agencyData.active_premium
                          ? formatter.format(
                            agencyData.active_premium
                          )
                          : 0}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Pending Cancellations:</span>
                      <span>
                        {agencyData.pending_cancellations
                          ? agencyData.pending_cancellations
                          : 0}
                      </span>
                    </div>
                  </div>
                  <div className={styles.rowInfo}>
                    <div className={styles.info}>
                      <span>Average Quotes per Month:</span>
                      <span>
                        {agencyData.average_quotes_per_month
                          ? agencyData.average_quotes_per_month
                          : 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.right}>
                {!isDisabled ? (
                  <IconedButton
                    size="150px"
                    color="#d32425"
                    textColor="white"
                    icon="log-out"
                    text="Impersonate"
                    marginRight
                    action={async () => {
                      setModal({
                        name: 'confirmation',
                        data: {
                          title: `Impersonate agency's owner`,
                          description: `Do you want to impersonate this agency's owner?`,
                        },
                        action: async () => {
                          setModal({});

                          try {
                            const res = await axios.post('/api/impersonate', {
                              impersonatableId: id,
                              type: 'account',
                            });

                            const receiver = document.getElementById('receiver').contentWindow;

                            receiver.postMessage(
                              res.data,
                              window.location.host === 'localhost:5002'
                                ? 'http://localhost:3000'
                                : 'https://app.coverbadger.com'
                            );

                            window.location.href =
                              window.location.host === 'localhost:5002'
                                ? 'http://localhost:3000/app/dashboard'
                                : 'https://app.coverbadger.com/app/dashboard';
                          } catch (err) {
                            console.log(err);
                            setModal('');
                          }

                          setModal('');
                        },
                      });
                    }}
                  />
                ) : null}
                <IconedButton
                  size="170px"
                  color="#d32425"
                  textColor="white"
                  icon="refresh-ccw"
                  text="Re-send invite"
                  marginRight
                  action={async () => {
                    await axios
                      .post('/api/invite', {
                        email: agencyData.owner_email,
                        agency_name: agencyData.name,
                        owner_name:
                          agencyData.owner_name.split(' ')[0] +
                          ' ' +
                          agencyData.owner_name.split(' ')[1],
                        type: 'agency',
                      })
                      .catch((error) => {
                        console.log(error);

                        context.notification.show(
                          'Failed to send the invite again.',
                          'failed',
                          true,
                          'toast',
                          'x-circle'
                        );
                      });

                    setModal({
                      name: 'success',
                      data: {
                        title: `Email sent!`,
                        description: `Email successfully sended to agency owner.`,
                      },
                      action: async () => {
                      },
                    });
                  }}
                />
                <IconedButton
                  size="150px"
                  color="#d32425"
                  textColor="white"
                  icon="airplay"
                  text="Pull Policy"
                  marginRight
                  action={async () => {
                    setModal({
                      name: 'pull_policy_modal',
                      data: {
                        agency_id: id,
                      },
                    });
                  }}
                />
                {agencyData.submissions_masked === true ? (
                  <IconedButton
                    size="230px"
                    color="#2ca312"
                    textColor="white"
                    icon="shield-off"
                    text="Unmask Submissions"
                    marginRight
                    action={() => {
                      setModal({
                        name: 'confirmation',
                        data: {
                          title: `Unmask agency's Submissions`,
                          description: `Do you want to unmask the submissions of this agency?`,
                        },
                        action: async () => {
                          setModal({});

                          await axios
                            .patch('/api/account/' + agencyData.id, {
                              submissions_masked: false,
                            })
                            .then((res) => {
                              window.location.reload();
                            })
                            .catch((error) => {
                              console.log(error);
                              setModal({});

                              context.notification.show(
                                'Failed to send the invite again.',
                                'failed',
                                true,
                                'toast',
                                'x-circle'
                              );
                            });
                        },
                      });
                    }}
                  />
                ) : (
                  <IconedButton
                    size="190px"
                    color="#000"
                    textColor="white"
                    icon="shield"
                    text="Mask Submissions"
                    marginRight
                    action={() => {
                      setModal({
                        name: 'confirmation',
                        data: {
                          title: `Mask agency's Submissions`,
                          description: `Do you want to mask the submissions of this agency?`,
                        },
                        action: async () => {
                          setModal({});

                          await axios
                            .patch('/api/account/' + agencyData.id, {
                              submissions_masked: true,
                            })
                            .then((res) => {
                              window.location.reload();
                            })
                            .catch((error) => {
                              console.log(error);
                              setModal({});

                              context.notification.show(
                                'Failed to send the invite again.',
                                'failed',
                                true,
                                'toast',
                                'x-circle'
                              );
                            });
                        },
                      });
                    }}
                  />
                )}
                {agencyData.status === 'partial_disabled' ||
                  agencyData.status === 'total_disabled' ? (
                  <IconedButton
                    size="170px"
                    color="#2ca312"
                    textColor="#fff"
                    icon="x-octagon"
                    text="Activate Agency"
                    action={() => {
                      setModal({
                        name: 'confirmation',
                        data: {
                          title: `Activate Agency`,
                          description: `Do you want to activate this agency?`,
                        },
                        action: async () => {
                          setModal({});

                          await axios
                            .patch('/api/account/' + agencyData.id, {
                              status: 'none',
                            })
                            .then((res) => {
                              window.location.reload();
                            })
                            .catch((error) => {
                              console.log(error);
                              setModal({});

                              context.notification.show(
                                'Failed to send the invite again.',
                                'failed',
                                true,
                                'toast',
                                'x-circle'
                              );
                            });
                        },
                      });
                    }}
                  />
                ) : (
                  <IconedButton
                    size="170px"
                    color="#000"
                    textColor="white"
                    icon="x-octagon"
                    text="Disable Agency"
                    action={() => {
                      setModal({
                        name: 'confirmation',
                        data: {
                          title: `Disable agency`,
                          description: `Do you want to disable this agency?`,
                        },
                        action: async () => {
                          setModal({});

                          await axios
                            .post(
                              '/api/account/disable/' + agencyData.id
                            )
                            .then((res) => {
                              window.location.reload();
                            })
                            .catch((error) => {
                              console.log(error);
                              setModal({});

                              context.notification.show(
                                'Failed to send the invite again.',
                                'failed',
                                true,
                                'toast',
                                'x-circle'
                              );
                            });
                        },
                      });
                    }}
                  />
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex' }}>
                <div
                  style={{
                    width: '150px',
                    backgroundColor: 'black',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    cursor: 'default',
                  }}
                >
                  Users
                </div>
                <div
                  onClick={() => {
                    window.location.href =
                      '/admin/agency/' + id + '/notes';
                  }}
                  style={{
                    backgroundColor: '#EAEAEA',
                    width: '150px',
                    color: '#090E3480',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  Notes
                </div>
                <div
                  onClick={() => {
                    window.location.href =
                      '/admin/agency/' + id + '/documents';
                  }}
                  style={{
                    backgroundColor: '#EAEAEA',
                    width: '150px',
                    color: '#090E3480',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  Documents
                </div>
                <div
                  onClick={() => {
                    window.location.href =
                      '/admin/agency/' + id + '/commissions';
                  }}
                  style={{
                    backgroundColor: '#EAEAEA',
                    width: '150px',
                    color: '#090E3480',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  Commissions
                </div>
              </div>
              <IconedButton
                size="130px"
                color="#d32425"
                textColor="white"
                icon="plus-circle"
                text="Add user"
                action={() => {
                  setModal({
                    name: 'user_modal',
                    agency_id: id,
                  });
                }}
              />
            </div>
            {window.location.host === 'localhost:5002' ? (
              <iframe
                id="receiver"
                src={`http://localhost:3000`}
                width="500"
                height="200"
                style={{ display: 'none' }}
              />
            ) : (
              <iframe
                id="receiver"
                src={`https://app.coverbadger.com`}
                width="500"
                height="200"
                style={{ display: 'none' }}
              />
            )}
            <Grid cols="1">
              <div className={styles.wrapper}>
                <div style={{ margin: 20 }}>
                  <div style={{ marginBottom: 30 }}>
                    <Search
                      callback={(query) => {
                        setSearchQueryString(query);
                      }}
                    />
                  </div>
                  <Table
                    loading={loading}
                    data={users}
                    badge={{
                      col: 'status',
                      condition: [
                        { value: 'Active', color: 'green' },
                        { value: 'Inactive', color: 'red' },
                      ],
                    }}
                    hide={['link', 'id']}
                    actions={{
                      custom: [
                        {
                          icon: 'refresh-ccw',
                          action: (row) => {
                            setModal({
                              name: 'confirmation',
                              data: {
                                title: `Re-send invite to user`,
                                description: `Do you want to re-send the invite to this user?`,
                              },
                              action: async () => {
                                setModal({});

                                await axios
                                  .post('/api/invite', {
                                    email: row.email,
                                    first_name: row.name.split('')[0],
                                    last_name: row.name.split('')[1],
                                    agency: id,
                                    type: 'user',
                                  })
                                  .then((r) => {
                                    setLoading(false);
                                    setModal('');

                                    context.notification.show(
                                      'Invite sent!',
                                      'success',
                                      true,
                                      'toast',
                                      'check-circle'
                                    );
                                  })
                                  .catch((error) => {
                                    console.log(error);

                                    setLoading(false);
                                    setModal('');

                                    context.notification.show(
                                      error.response.data.message,
                                      'failed',
                                      true,
                                      'toast',
                                      'x-circle'
                                    );
                                  });

                                setModal('');
                              },
                            });
                          },
                          color: '#637381',
                          tooltip: 'Re-send invite',
                        },
                        {
                          icon: 'log-out',
                          action: (row) => {
                            if (!row.id) return;

                            setModal({
                              name: 'confirmation',
                              data: {
                                title: `Impersonate user`,
                                description: `Do you want to impersonate this user?`,
                              },
                              action: async () => {
                                setModal({});

                                axios
                                  .post('/api/impersonate', {
                                    impersonatableId: row.id,
                                    type: 'user',
                                  })
                                  .then((res) => {
                                    //send message
                                    var receiver =
                                      document.getElementById(
                                        'receiver'
                                      ).contentWindow;

                                    receiver.postMessage(
                                      res.data,
                                      'https://app.coverbadger.com'
                                    );

                                    window.location.href =
                                      'https://app.coverbadger.com/app/dashboard';
                                  })
                                  .catch((err) => {
                                    console.log(err);
                                    setModal('');
                                  });
                                setModal('');
                              },
                            });
                          },
                          color: '#637381',
                          tooltip: 'Impersonate',
                        },
                        {
                          icon: 'trash',
                          action: (row) => {
                            if (!row.id) return;

                            // Check if user is already inactive
                            if (row.status === 'Inactive') {
                              context.notification.show(
                                'This user has already been deleted',
                                'error',
                                true,
                                'toast',
                                'x-circle'
                              );
                              return;
                            }

                            if (
                              row.permission.props &&
                              row.permission.props.text === 'owner'
                            ) {
                              setModal({
                                name: 'warning',
                                data: {
                                  title: `You can't delete this user`,
                                  description:
                                    "You can't delete this user because he is the agency's owner",
                                  buttonLabel: `Close`,
                                },
                                action: async () => {
                                  setModal({});
                                },
                              });
                            } else {
                              setModal({
                                name: 'confirmation',
                                data: {
                                  title: `Delete ${row.isInvite ? 'Invitation' : 'User'}`,
                                  description: `
                                    Do you want to delete this
                                    ${row.isInvite ? 'invitation' : 'user'}?
                                    `,
                                },
                                action: async () => {
                                  setModal('');
                                  setLoading(true);

                                  try {
                                    if (row.status === "Invited") {
                                      await axios.delete(`/api/invite/${row.id}`);
                                    } else {
                                      await axios.delete(`/api/user/${row.id}`);
                                    }

                                    await runAPI(page, searchQueryString);

                                    context.notification.show(
                                      row.isInvite ? 'Invitation deleted!' : 'User deleted!',
                                      'success',
                                      true,
                                      'toast',
                                      'check-circle'
                                    );
                                  } catch (error) {
                                    console.error(error);
                                    setModal('');

                                    context.notification.show(
                                      `Failed to delete ${row.isInvite ? 'invitation' : 'user'}`,
                                      'error',
                                      false,
                                      'toast',
                                      'x-circle'
                                    );
                                  } finally {
                                    setLoading(false);
                                  }
                                },
                              });
                            }
                          },
                          color: '#637381',
                          tooltip: 'Delete',
                        },
                      ],
                    }}
                  />
                  {!loading && payload && (
                    <Pagination
                      pagesCount={payload.pages_count}
                      active={payload.page}
                      onChange={async (newPage) => {
                        setPage(newPage);
                        runAPI(newPage, searchQueryString);
                      }}
                    />
                  )}
                </div>
              </div>
            </Grid>
          </Animate>
        </>
      )}
    </>
  );
}